import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import ChatGPTMappingEditor from './ChatGPTMappingEditor';

// Admin config untuk scrape Agent AI module FLOWNITY.
// Platform: ChatGPT (custom GPT) atau Gemini (Gem) — config berasingan, disimpan dalam
// modules/flownity.scrapeConfig.chatgpt / modules/flownity.scrapeConfig.gemini.
// Field `gptUrl` = URL agent generik (custom GPT ATAU Gem) — nama dikekalkan untuk backward compat.
// Custom GPT / Gem bawa master prompt; SESAAT cuma submit detail produk + scrape JSON output → letak kat card.

const DEFAULT_PROMPT = 'Nama produk: {productName}\nLink produk: {productLink}\n\nKeluarkan output dalam format JSON yang diminta.';

const PLATFORMS = {
    chatgpt: {
        label: 'ChatGPT',
        color: '#10a37f',
        bg: '#d1fae5',
        urlLabel: 'URL Custom GPT',
        urlPlaceholder: 'https://chatgpt.com/g/g-xxx-flownity',
        sendLabel: 'Ayat hantar ke GPT',
        defaultDisplayName: 'ChatGPT (GPTs)',
    },
    gemini: {
        label: 'Gemini (Gems)',
        color: '#3b82f6',
        bg: '#dbeafe',
        urlLabel: 'URL Gem',
        urlPlaceholder: 'https://gemini.google.com/gem/xxxxxxxxxxxx',
        sendLabel: 'Ayat hantar ke Gem',
        defaultDisplayName: 'Gemini (Gems)',
    },
};

// Default mapping = identity (set1.<field> ← $.set1.<field>) untuk N babak.
// Sebab output GPT FLOWNITY guna nama field sama dengan card (promptVideoScene1, videoScript1, captionTiktok).
function buildDefaultMapping(sceneCount) {
    const n = Math.max(2, Math.min(8, Number(sceneCount) || 5));
    const m = {};
    // Target = field card (set1.promptVideo${i} / set1.videoScript${i}); sumber = output GPT (promptVideoScene${i}).
    for (let i = 1; i <= n; i++) {
        m[`set1.promptVideo${i}`] = `$.set1.promptVideoScene${i}`;
        m[`set1.videoScript${i}`] = `$.set1.videoScript${i}`;
    }
    m['set1.captionTiktok'] = '$.set1.captionTiktok';
    return JSON.stringify(m, null, 2);
}

const emptyCfg = (sc) => ({ gptUrl: '', displayName: '', promptTemplate: DEFAULT_PROMPT, mapping: buildDefaultMapping(sc) });

const FlownityScrapeConfig = ({ moduleId = 'flownity' }) => {
    const [sceneCount, setSceneCount] = useState(5);
    const [platform, setPlatform] = useState('chatgpt');
    // configs[platform] = { gptUrl, promptTemplate, mapping }
    const [configs, setConfigs] = useState({ chatgpt: emptyCfg(5), gemini: emptyCfg(5) });
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savedTick, setSavedTick] = useState(0);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'modules', moduleId));
                if (!alive) return;
                const data = snap.exists() ? snap.data() : {};
                const sc = data.sceneCount || 5;
                setSceneCount(sc);
                const loadCfg = (raw) => ({
                    gptUrl: raw?.gptUrl || '',
                    displayName: raw?.displayName || '',
                    promptTemplate: raw?.promptTemplate || DEFAULT_PROMPT,
                    mapping: raw?.mapping || buildDefaultMapping(sc),
                });
                setConfigs({
                    chatgpt: loadCfg(data.scrapeConfig?.chatgpt),
                    gemini: loadCfg(data.scrapeConfig?.gemini),
                });
            } catch (e) {
                console.error('[FlownityScrapeConfig] load failed:', e);
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [moduleId]);

    const cfg = configs[platform];
    const meta = PLATFORMS[platform];
    const setCfgField = (field, value) => {
        setConfigs(prev => ({ ...prev, [platform]: { ...prev[platform], [field]: value } }));
    };

    const handleSave = async () => {
        if (!cfg.gptUrl.trim()) { alert(`${meta.urlLabel} wajib diisi.`); return; }
        if (cfg.mapping.trim()) {
            try { JSON.parse(cfg.mapping); } catch (e) { alert('Mapping bukan JSON valid: ' + e.message); return; }
        }
        setSaving(true);
        try {
            await updateDoc(doc(db, 'modules', moduleId), {
                [`scrapeConfig.${platform}`]: {
                    gptUrl: cfg.gptUrl.trim(),
                    displayName: cfg.displayName.trim(),
                    promptTemplate: cfg.promptTemplate,
                    mapping: cfg.mapping.trim(),
                },
            });
            setSavedTick(t => t + 1);
        } catch (e) {
            alert('Gagal simpan: ' + e.message);
        } finally {
            setSaving(false);
        }
    };

    const lbl = { fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 4 };
    const inp = { width: '100%', fontSize: 12, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontFamily: 'monospace', boxSizing: 'border-box' };

    if (!loaded) {
        return <div style={{ marginTop: 20, fontSize: 11, color: '#94a3b8' }}>Memuatkan config scrape...</div>;
    }

    return (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 18, color: meta.color }}>smart_toy</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#1e293b' }}>Scrape Agent AI</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: meta.color, background: meta.bg, padding: '2px 6px', borderRadius: 4 }}>ADMIN</span>
                <div style={{ flex: 1 }}></div>
                <select
                    value={platform}
                    onChange={e => setPlatform(e.target.value)}
                    style={{ fontSize: 11, fontWeight: 700, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc', color: '#334155', cursor: 'pointer' }}
                >
                    {Object.entries(PLATFORMS).map(([id, p]) => (
                        <option key={id} value={id}>{p.label}{configs[id].gptUrl ? ' ✓' : ''}</option>
                    ))}
                </select>
            </div>

            <div style={{ marginBottom: 12 }}>
                <label style={lbl}>{meta.urlLabel}</label>
                <input type="url" value={cfg.gptUrl} onChange={e => setCfgField('gptUrl', e.target.value)} placeholder={meta.urlPlaceholder} style={inp} />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label style={lbl}>
                    Nama Paparan
                    <span style={{ textTransform: 'none', fontWeight: 500, color: '#94a3b8' }}> — nama dalam dropdown workspace. Kosong = guna "{meta.defaultDisplayName}"</span>
                </label>
                <input type="text" value={cfg.displayName} onChange={e => setCfgField('displayName', e.target.value)} placeholder={meta.defaultDisplayName} maxLength={40} style={inp} />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label style={lbl}>
                    {meta.sendLabel}
                    <span style={{ textTransform: 'none', fontWeight: 500, color: '#94a3b8' }}> — {'{productName}'} &amp; {'{productLink}'} auto-isi masa run</span>
                </label>
                <textarea value={cfg.promptTemplate} onChange={e => setCfgField('promptTemplate', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} />
            </div>

            <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Mapping Field</label>
                <ChatGPTMappingEditor value={cfg.mapping} onChange={val => setCfgField('mapping', val)} sceneCount={sceneCount} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ fontSize: 12, fontWeight: 700, color: '#fff', background: saving ? '#94a3b8' : meta.color, border: 'none', borderRadius: 8, padding: '8px 16px', cursor: saving ? 'default' : 'pointer' }}
                >
                    {saving ? 'Menyimpan...' : `💾 Simpan (${meta.label})`}
                </button>
                {savedTick > 0 && !saving && <span style={{ fontSize: 11, color: meta.color, fontWeight: 600 }}>✓ Disimpan</span>}
            </div>
        </div>
    );
};

export default FlownityScrapeConfig;
